import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

const BASE_URL = `${API_BASE_PATH}/Target`;
const INCENTIVE_URL = `${API_BASE_PATH}/incentive`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  };
};

const showAxiosError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail || error?.message || fallback;
  console.error(detail);
};

const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};


// ✅ Interface
export interface Incentive {
  id: number;
  user_id: string;
  user_name: string;
  salary: number;
  target: number;
  incentive_target: number;
  total_deal_value: number;
  incentive_percentage: number;
  calculated_incentive: number;
  incentive_level?: string;
  created_at:string;
  incentive_achieved_amount?:string
}

// ✅ Interface (message fallback)
export interface IncentiveFallback {
  user_id: string;
  user_name: string;
  message: string;
}

// ✅ Create Target
export const createTarget = async (form: {
  user_id: string;
  salary: number;
}) => {
  try {
    const formData = new URLSearchParams({
      user_id: form.user_id,
      salary: form.salary.toString(),
    });

    const res = await axios.post(`${BASE_URL}/post`, formData, {
      headers: getAuthHeaders(),
    });

    // toast.success("Target created");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to create Target");
    throw error;
  }
};


// ✅ Delete Target
export const deleteTarget = async (id: string) => {
  try {
    const res = await axios.delete(`${API_BASE_PATH}/delete-target/${id}`, {
      headers: getAuthHeaders(),
    });

    // toast.success("Target deleted");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to delete Target");
    throw error;
  }
};

// ✅ Get Incentive by User ID
export const getIncentiveByUser = async (
  user_id: string
): Promise<Incentive> => {
  try {
    const res = await axios.get(`${INCENTIVE_URL}/calculate/per/user/${user_id}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch incentive data");
    throw error;
  }
};

// ✅ Get Incentives for All Users
export const getAllIncentives = async (): Promise<Incentive[]> => {
  try {
    const res = await axios.get(`${INCENTIVE_URL}/calculate/all`, {
      headers: getAuthHeaders(),
    });

    return res.data ?? []; // ✅ correct structure now
  } catch (error) {
    showAxiosError(error, "Failed to fetch incentives");
    throw error;
  }
};



/* =====================================================
   EXPORT – ALL
===================================================== */

// ✅ Export all Targets – Excel
export const exportTargetExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/Target/export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Targets exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export targets (Excel)");
    throw error;
  }
};

// ✅ Export all Targets – PDF
export const exportTargetPDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/Target/export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Targets exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export targets (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Targets – Excel
export const exportSelectedTargetExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/Target/export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected targets exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected targets (Excel)"
    );
    throw error;
  }
};

// ✅ Export selected Targets – PDF
export const exportSelectedTargetPDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/Target/export_selected_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected targets exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected targets (PDF)"
    );
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Targets
export const deleteTargetBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    const res = await axios.delete(
      `${API_BASE_PATH}/Target/bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    // toast.success(
    //   res.data?.message || "Targets deleted successfully"
    // );
  } catch (error: any) {
    if (error?.response?.status === 403) {
      console.error("You are not authorized to delete targets.");
    } else {
      handleAxiosError(error, "Failed to delete targets");
    }
    throw error;
  }
};